/**
 * SkuVolumeAudit — health screen for the sku.volume_m3 + units_per_pallet
 * fields. These two numbers drive the inbound-capacity projections; when
 * they're wrong the /pallets banner screams nonsense percentages.
 *
 * The page renders a KPI band + four drill-down tables (missing, tiny,
 * absurd, missing_units_per_pallet) + a "Download CSV template" and
 * "Upload updated CSV" workflow.
 */
import React, { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import {
  Warning, CheckCircle, ArrowsClockwise, DownloadSimple, UploadSimple, Ruler, Cube,
} from '@phosphor-icons/react';
import {
  getSkuVolumeAudit, downloadSkuVolumeAuditTemplate, uploadSkuVolumeAudit,
} from '../lib/api';

const BUCKETS = [
  { key: 'missing', label: 'Missing volume', color: 'rose', desc: 'volume_m3 is blank or zero' },
  { key: 'tiny', label: 'Suspiciously small', color: 'amber', desc: '< 10 cm³ — likely a data-entry typo' },
  { key: 'absurd', label: 'Suspiciously huge', color: 'amber', desc: '> 4 m³ — probably in cm not m' },
  { key: 'missing_units_per_pallet', label: 'Missing units/pallet', color: 'sky', desc: 'pallet-fit unknown; projections use a 40-cartons/pallet fallback' },
];

export default function SkuVolumeAudit() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [selectedBucket, setSelectedBucket] = useState('missing');

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await getSkuVolumeAudit();
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load audit');
    } finally { setLoading(false); }
  };

  useEffect(() => { refresh(); }, []);

  const handleDownload = async () => {
    try {
      const r = await downloadSkuVolumeAuditTemplate();
      const blob = new Blob([r.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'sku-volume-audit-template.csv';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Template downloaded — fill in the blanks and re-upload.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Download failed');
    }
  };

  const handleUpload = async (e, dryRun) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setUploadResult(null);
    try {
      const r = await uploadSkuVolumeAudit(file, dryRun);
      setUploadResult(r.data);
      toast.success(r.data.message);
      if (!dryRun) await refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const summary = data?.summary;
  const problems = data?.problems || {};
  const selectedRows = problems[selectedBucket] || [];

  return (
    <Layout>
      <div className="max-w-7xl mx-auto p-6 space-y-4" data-testid="sku-volume-audit-page">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3">
              <Ruler size={28} weight="fill" className="text-brand" /> SKU Volume Audit
            </h1>
            <p className="text-sm text-muted-foreground">
              Clean up the volumes and pallet-fits driving your capacity projections. Download the CSV,
              fix blanks, upload back — 30-second cycle.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={refresh} data-testid="audit-refresh-btn">
              <ArrowsClockwise size={14} className="mr-1" /> Refresh
            </Button>
            <Button variant="outline" size="sm" onClick={handleDownload} data-testid="audit-download-template-btn">
              <DownloadSimple size={14} className="mr-1" /> Download CSV template
            </Button>
            <label className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold border border-brand bg-brand text-brand-foreground rounded cursor-pointer hover:opacity-90" data-testid="audit-upload-label">
              <UploadSimple size={14} /> {uploading ? 'Uploading…' : 'Upload updated CSV'}
              <input type="file" accept=".csv" className="hidden" onChange={(e) => handleUpload(e, false)} data-testid="audit-upload-input" />
            </label>
          </div>
        </div>

        {loading && <div className="text-sm text-muted-foreground">Auditing SKUs…</div>}

        {summary && (
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3" data-testid="audit-kpis">
            <KpiTile icon={CheckCircle} color="emerald" label="Healthy" value={summary.ok} sub={`${summary.ok_pct}% of catalog`} testId="kpi-audit-ok" />
            <KpiTile icon={Warning} color="rose" label="Missing volume" value={summary.missing} testId="kpi-audit-missing" />
            <KpiTile icon={Warning} color="amber" label="Suspicious size" value={summary.tiny + summary.absurd} sub={`${summary.tiny} tiny · ${summary.absurd} huge`} testId="kpi-audit-suspicious" />
            <KpiTile icon={Cube} color="sky" label="No pallet-fit" value={summary.missing_units_per_pallet} testId="kpi-audit-missing-upp" />
            <KpiTile icon={Ruler} color="violet" label="Total SKUs" value={summary.total} testId="kpi-audit-total" />
            <KpiTile icon={CheckCircle} color="emerald" label="Overall health" value={`${summary.ok_pct}%`} sub={summary.ok_pct >= 95 ? 'Great' : summary.ok_pct >= 70 ? 'Getting there' : 'Needs attention'} testId="kpi-audit-health" />
          </div>
        )}

        {uploadResult && (
          <div
            data-testid="audit-upload-result"
            className={`border p-3 text-xs ${uploadResult.errors?.length ? 'border-amber-300 bg-amber-50 text-amber-900' : 'border-emerald-300 bg-emerald-50 text-emerald-900'}`}
          >
            <div className="font-bold">Result</div>
            <div>Processed: {uploadResult.processed} · Updated: {uploadResult.updated} · Skipped (no change): {uploadResult.skipped_no_change} · Errors: {uploadResult.errors?.length || 0}</div>
            {uploadResult.errors?.length > 0 && (
              <ul className="mt-1 list-disc pl-4 max-h-24 overflow-auto">
                {uploadResult.errors.slice(0, 20).map((e, i) => (
                  <li key={i}>Row {e.row}{e.sku_code ? ` (${e.sku_code})` : ''}: {e.error}</li>
                ))}
              </ul>
            )}
          </div>
        )}

        {summary && (
          <div className="border border-border">
            <div className="flex border-b border-border overflow-x-auto" data-testid="audit-bucket-tabs">
              {BUCKETS.map((b) => (
                <button
                  key={b.key}
                  onClick={() => setSelectedBucket(b.key)}
                  data-testid={`audit-bucket-${b.key}`}
                  className={`px-4 py-2 text-xs font-semibold border-b-2 -mb-px transition ${
                    selectedBucket === b.key ? 'border-foreground text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {b.label} <span className="ml-1 font-mono">({summary[b.key] ?? 0})</span>
                </button>
              ))}
            </div>
            <div className="p-3">
              <div className="text-xs text-muted-foreground mb-2">{BUCKETS.find((b) => b.key === selectedBucket)?.desc}</div>
              {selectedRows.length === 0 ? (
                <div className="text-sm text-emerald-800 p-3 border border-emerald-200 bg-emerald-50 flex items-center gap-2">
                  <CheckCircle size={14} weight="fill" /> Nothing in this bucket — you&apos;re clean.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="text-left px-2 py-1.5 text-[10px] uppercase tracking-widest">SKU code</th>
                        <th className="text-left px-2 py-1.5 text-[10px] uppercase tracking-widest">Name</th>
                        <th className="text-left px-2 py-1.5 text-[10px] uppercase tracking-widest">UOM</th>
                        <th className="text-right px-2 py-1.5 text-[10px] uppercase tracking-widest">Volume m³</th>
                        <th className="text-right px-2 py-1.5 text-[10px] uppercase tracking-widest">Units/pallet</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedRows.map((r) => (
                        <tr key={r.id} className="border-b border-border/50" data-testid={`audit-row-${r.id}`}>
                          <td className="px-2 py-1.5 font-mono">{r.sku_code}</td>
                          <td className="px-2 py-1.5">{r.name}</td>
                          <td className="px-2 py-1.5 text-muted-foreground">{r.unit_of_measure || '—'}</td>
                          <td className="px-2 py-1.5 text-right font-mono">{r.volume_m3 ?? '—'}</td>
                          <td className="px-2 py-1.5 text-right font-mono">{r.units_per_pallet ?? '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {selectedRows.length >= 200 && (
                    <div className="text-[11px] text-muted-foreground mt-2">Showing first 200 — download the CSV template to see and edit the rest.</div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

function KpiTile({ icon: Icon, label, value, sub, color = 'sky', testId }) {
  const colorMap = {
    sky: 'text-sky-700 bg-sky-50 border-sky-200',
    emerald: 'text-emerald-700 bg-emerald-50 border-emerald-200',
    amber: 'text-amber-700 bg-amber-50 border-amber-200',
    rose: 'text-rose-700 bg-rose-50 border-rose-200',
    violet: 'text-violet-700 bg-violet-50 border-violet-200',
  };
  return (
    <div data-testid={testId} className={`border ${colorMap[color]} p-3 flex flex-col gap-1`}>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest font-semibold opacity-80">
        <Icon size={12} weight="bold" /> {label}
      </div>
      <div className="text-2xl font-bold font-mono">{value}</div>
      {sub && <div className="text-[10px] text-muted-foreground">{sub}</div>}
    </div>
  );
}
